import { clientConfig } from '@/config/clientConfig';
import { ApiCurrencyCode, useApi } from '@/shared/api';
import { useApiMyhub } from '@/shared/apiMyhub/useApiMyhub';
import { useCurrencyRatesStore } from '@/shared/store/currencyRates.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { log } from '@/shared/utils/log';
import { withResolvers } from '@/shared/utils/promises';

const EXP_TIME = 12 * 60 * 60 * 1000; // 12h

export type Price = {
  currency: string;
  amount: number;
};

const cache = new Map<string, Promise<number>>();
const makePairKey = (base: string, quote: string) => `${base}_${quote}`;

export const useCurrencyRates = () => {
  const profileBaseCurrency = useProfileStore((s) => s.baseCurrency);
  const { getRate, setRate } = useCurrencyRatesStore();
  const api = useApi();
  const apiMyhub = useApiMyhub();

  const fetchCurrencyRate = async (baseCurrency: string, quoteCurrency: string) => {
    const storedRateInfo = getRate(baseCurrency, quoteCurrency);
    let rate: number | null = null;

    if (clientConfig.isExported) {
      return await apiMyhub.getCurrencyRate(baseCurrency, quoteCurrency);
    }

    if (storedRateInfo === undefined || Date.now() > storedRateInfo.ts + EXP_TIME) {
      const res = await api.getCurrencyRate({
        baseCurrency: baseCurrency as ApiCurrencyCode,
        // @ts-ignore remove after api fix typing
        quoteCurrencies: [quoteCurrency] as ApiCurrencyCode[],
      });

      if (res.data) {
        rate = res.data[0].rate;
        setRate(baseCurrency, quoteCurrency, { rate, ts: Date.now() });
      } else {
        log.warn(`[useCurrencyRates] Fail to fetch rate. ${baseCurrency}/${quoteCurrency}`);
      }
    } else {
      rate = storedRateInfo.rate;
    }

    return rate;
  };

  const getCurrencyRate = async (baseCurrency: string, quoteCurrency: string) => {
    const pairKey = makePairKey(baseCurrency, quoteCurrency);

    if (cache.has(pairKey)) {
      return cache.get(pairKey);
    }

    const resolvers = withResolvers<number>();
    cache.set(pairKey, resolvers.promise);
    const rate = await fetchCurrencyRate(baseCurrency, quoteCurrency);

    if (rate) {
      resolvers.resolve(rate);
      return cache.get(pairKey);
    } else {
      return null;
    }
  };

  const sumPricesList = async (list: Price[]) => {
    const targetCurrency = profileBaseCurrency ?? clientConfig.defaultProfileBaseCurrency;
    const res = await Promise.allSettled(
      list.map(({ currency, amount }) => calculateInBaseCurrency(currency, amount)),
    );

    const sum = res.reduce((acc, cur) => {
      if (cur.status === 'fulfilled') {
        if (cur.value.currency === targetCurrency) {
          return acc + cur.value.amount;
        } else {
          log.info(
            `[useCurrencyRates] sumPricesList skip. ${profileBaseCurrency}/${cur.value.currency}`,
          );
          return acc;
        }
      } else {
        log.warn(
          `[useCurrencyRates] sumPricesList skip. status: ${cur.status} reason: ${cur.reason}`,
        );
        return acc;
      }
    }, 0);

    return {
      currency: targetCurrency,
      amount: sum,
    };
  };

  const calculateInBaseCurrency = async (currency: string, amount: number) => {
    const targetCurrency = profileBaseCurrency ?? clientConfig.defaultProfileBaseCurrency;

    if (currency === targetCurrency) {
      return { currency, amount };
    }

    let resultAmount: number;
    let resultCurrency: string;

    const rate = await getCurrencyRate(targetCurrency, currency);

    if (rate) {
      resultCurrency = targetCurrency;
      resultAmount = amount / rate;
    } else {
      resultCurrency = currency;
      resultAmount = amount;
    }

    return {
      currency: resultCurrency,
      amount: resultAmount,
    };
  };

  return {
    calculateInBaseCurrency,
    sumPricesList,
    getCurrencyRate,
  };
};
